/*
 * The public legal pages (stage 5, App Store): one source for the static pages the Worker serves at /legal/* without the
 * staging password (built by vite.config.ts) and for the same pages inside the app (screens/Legal.tsx).
 *
 * DRAFTS. Everything between [[ and ]] is a placeholder Saif must fill in, and the whole text needs checking by his
 * adviser before launch (TODO-SAIF "Stage 5: App Store"). Plain text only: a line starting "- " is a bullet.
 */

export interface LegalSection {
  heading: string;
  body: string[];
}
export interface LegalDoc {
  slug: 'privacy' | 'terms' | 'credit' | 'rewards' | 'catering' | 'support';
  title: string;
  summary: string;
  updated: string;
  sections: LegalSection[];
}

export const LEGAL_UPDATED = '25 October 2026';
export const CONTROLLER = 'Tikkas Group Limited';
const CONTACT = 'hello@tikkas.com';
const PRIVACY_CONTACT = 'privacy@tikkas.com';
const SHOP = '1 Great Titchfield St, London W1W 8AU';
const SITE_COMPANY = 'Tikkas Oxford Circus Ltd';

export const LEGAL_DOCS: LegalDoc[] = [
  {
    slug: 'privacy',
    title: 'Privacy policy',
    summary: 'What we collect when you use the Tikkas app and website, why, how long we keep it, who helps us, and your rights.',
    updated: LEGAL_UPDATED,
    sections: [
      {
        heading: 'Who we are',
        body: [
          `The Tikkas app and app.tikkas.com are run by ${CONTROLLER}, a company registered in England and Wales (company number 16132072), registered office 1 Great Titchfield Street, London, England, W1W 8AU. We are the controller of the personal data described here. We registered with the Information Commissioner's Office on 24 October 2026; our ICO registration number will be shown here as soon as it is issued.`,
          `Your food is made and sold by ${SITE_COMPANY}, which runs the shop at ${SHOP}. It receives the details of your orders so it can make them, take payment and keep its accounts.`,
          `Questions about your data: ${PRIVACY_CONTACT}.`,
        ],
      },
      {
        heading: 'What we collect',
        body: [
          '- Your mobile number, to sign you in with a text code and to keep one account per number.',
          '- Your name, and your email address if you add one.',
          '- Your orders: what you ordered, when, the pickup time, any note or allergy information you give us, how you paid (for cards, only the brand and last four digits: Stripe holds the card itself), refunds, and orders you link at the till by scanning your code.',
          '- Your Rewards Club points, store credit, rewards and codes used, and the history of each.',
          '- Catering orders: company name, delivery address and access notes, the contact name and mobile for the day, the invoice email, a PO number, names and dietary needs on team orders, and messages with our team.',
          '- How you use the app: screens opened, items added to the bag, orders started and finished, messages seen or tapped, with a random identifier for your device. We use this to run and improve the app and to send relevant offers if you have agreed to them.',
          '- Push notification details: a token for each device where you allow notifications.',
          '- Your marketing choices for email, text and push, and every change to them, with the time and where you made it.',
          '- Technical details: your IP address and browser or phone type, used for security and to stop abuse.',
          '- Where you use the app from, roughly: when you sign in, open the app or place an order, our network provider (Cloudflare) tells us the country, town and the first part of the postcode (such as "W1") that your connection appears to come from. We keep that, with the date, so we can see which areas our customers come from. We do not use your phone\'s location, we never ask for it, and we do not keep your IP address with it.',
          '- Ratings and comments you leave about an order.',
        ],
      },
      {
        heading: 'Why we use it',
        body: [
          '- To give you the service you asked for (our contract with you): your account, orders, pickup, catering, the Rewards Club and store credit, and receipts and order updates such as "your order is ready".',
          '- Because we have a legitimate interest: keeping accounts secure, preventing fraud and misuse of codes and rewards, fixing problems, and understanding how the app is used so we can improve it.',
          '- With your consent: offers and news by email, text or push. Email and text are off until you switch them on. For push, the app shows you a screen that says notifications include offers before your phone asks you to allow them: allowing notifications switches on order updates and offers; turn offers off any time in Account. You can switch any of them off at any time in Account, by tapping unsubscribe in an email, or by replying STOP to a text.',
          '- To meet legal duties: tax and accounting records, and answering lawful requests.',
          'Order updates and receipts are not marketing and are sent whatever your marketing choices. You can turn order notifications off in Account.',
          'We do not sell your data, we do not show third-party adverts, and we do not track you across other companies\' apps or websites.',
        ],
      },
      {
        heading: 'Who helps us',
        body: [
          'These companies process data for us under contract, only to provide their service:',
          '- Cloudflare: hosting, the database and file storage for the app and website.',
          '- Stripe: card payments and saved cards. Stripe also acts as an independent controller for fraud prevention and its legal duties.',
          '- Twilio: sign-in codes by text, and marketing texts if you agree to them.',
          '- Resend: receipts, catering emails and, if you agree, marketing emails.',
          '- Google: address lookup when you order catering, and Firebase Cloud Messaging, which delivers push notifications to the app.',
          '- Apple: delivers push notifications to iPhones.',
          'Some of these providers may handle data outside the UK. Where they do, they are bound by the UK\'s approved safeguards, such as the International Data Transfer Agreement or the UK addendum to the EU standard contractual clauses.',
        ],
      },
      {
        heading: 'How long we keep it',
        body: [
          '- Your account, points and credit: while your account is open.',
          '- When you delete your account (Account → Delete account), your name, number, email, push tokens and usage history are deleted or made anonymous straight away, and marketing stops.',
          '- Order, payment and invoice records: six years, because tax law requires it. After deletion they no longer identify you.',
          '- Your consent history, and the email address or number of anyone who asked us to stop messaging them: kept so we can prove consent and honour the opt-out.',
          '- Sign-in codes: 10 minutes. Signed-in sessions: up to 180 days, or until you sign out.',
          '- Usage events: 24 months.',
        ],
      },
      {
        heading: 'Your rights',
        body: [
          'Under UK data protection law you can ask for a copy of your data, ask us to correct it, delete it, restrict or object to how we use it, or to send it to you or another company in a common format. Where we rely on your consent you can withdraw it at any time.',
          `To use any of these rights, email ${PRIVACY_CONTACT}. We reply within one month.`,
          'If you are unhappy with how we handle your data, please tell us first. You can also complain to the Information Commissioner\'s Office: ico.org.uk, 0303 123 1113.',
        ],
      },
      {
        heading: 'Cookies and storage on your device',
        body: [
          'The website uses one essential cookie to keep you signed in. The app keeps your sign-in in your phone\'s secure keychain. Both keep your bag, your preferences (such as dark mode) and a random device identifier on your device. We use no advertising or third-party tracking cookies. Stripe sets its own cookies when you pay, to prevent fraud.',
        ],
      },
      {
        heading: 'Children',
        body: ['Tikkas accounts are for people aged 16 and over. Anyone can browse the menu and order without an account.'],
      },
      {
        heading: 'Changes',
        body: [`If we change this policy we will update the date at the top and, for important changes, tell you in the app. Last updated ${LEGAL_UPDATED}.`],
      },
    ],
  },
  {
    slug: 'terms',
    title: 'Terms of use',
    summary: 'The rules for using the Tikkas app and website and ordering from us.',
    updated: LEGAL_UPDATED,
    sections: [
      {
        heading: 'Who we are',
        body: [
          `The app and app.tikkas.com are run by ${CONTROLLER} (company number 16132072, 1 Great Titchfield Street, London, England, W1W 8AU). Food is sold by ${SITE_COMPANY}, ${SHOP}. "We" means both.`,
          `Contact: ${CONTACT}, or ask at the counter.`,
        ],
      },
      {
        heading: 'Your account',
        body: [
          'You sign in with your UK mobile number and a code we text you. There is one account per number. Keep your phone secure: anyone who can read your texts or open your app can use your account.',
          'You can browse the menu without an account. You need one to order, earn points or hold store credit.',
          'You can delete your account at any time in Account → Delete account. Spend any store credit first: it is lost when the account goes (see the store credit terms).',
        ],
      },
      {
        heading: 'Ordering and paying',
        body: [
          '- Prices are in pounds and include VAT. The total you see at checkout is worked out by our system and is what you pay.',
          '- Your order is accepted when it is paid (or fully covered by credit or a reward) and you see the order screen. If something goes wrong taking payment, the order is not placed and nothing is charged.',
          '- You can pay by card, with a card you saved, or with store credit, or credit and a card together. Card payments are handled by Stripe; we never see or store your full card number.',
          '- Some codes give money off or a free item; the code decides the offer. Codes cannot be swapped for cash and may have limits, which the app shows.',
        ],
      },
      {
        heading: 'Collecting your order',
        body: [
          '- Order for as soon as possible or for a later time. When you order while we are closed, it is for the next time we are open.',
          '- Show the code in the app (or tell us the backup code on your order screen) at the counter to collect. Rewards Club points are added when you collect.',
          '- Please collect within 30 minutes of the ready time. We cannot keep food beyond that for food-safety reasons.',
        ],
      },
      {
        heading: 'Allergies and dietary needs',
        body: [
          'Our chicken is halal (HMC certified). Allergen information is shown for every item and is available in the shop. Our kitchen handles all 14 major allergens, so we cannot guarantee any item is free of them. If you have an allergy, add it to your order and speak to the team before eating.',
        ],
      },
      {
        heading: 'If something is wrong',
        body: [
          'Tell the team at the counter or contact us and we will put it right. A refund goes back to the card first and then to store credit, in the same proportions you paid. Points from a refunded order come off; points you spent on it come back. This does not affect your legal rights under the Consumer Rights Act 2015.',
        ],
      },
      {
        heading: 'Using the app fairly',
        body: [
          'Please do not misuse the app: no automated ordering, no attempts to get rewards, codes or credit you are not entitled to, no interfering with the service. We may suspend or close an account that does, and cancel rewards gained that way.',
          'We work hard to keep the app running but cannot promise it will always be available. We may change or stop features.',
        ],
      },
      {
        heading: 'Our responsibility to you',
        body: [
          'We are responsible for losses you suffer that were a foreseeable result of us breaking these terms or failing to use reasonable care. We do not exclude liability where the law does not allow it, including for death or personal injury caused by our negligence. The app is for personal and office use, not for resale.',
        ],
      },
      {
        heading: 'Other terms',
        body: [
          'The Rewards Club terms, the store credit terms and, for catering, the catering terms also apply. Apple and Google are not parties to these terms and are not responsible for the app or our food.',
          'These terms are governed by the law of England and Wales, and you can bring a claim in the courts of the part of the UK where you live.',
          `Last updated ${LEGAL_UPDATED}.`,
        ],
      },
    ],
  },
  {
    slug: 'credit',
    title: 'Store credit terms',
    summary: 'How Tikkas store credit works: topping up, spending, bonuses and refunds.',
    updated: LEGAL_UPDATED,
    sections: [
      {
        heading: 'What store credit is',
        body: [
          'Store credit is money you pay us in advance to spend on food and drink at Tikkas: in the app, on the website, and at the till when you scan your code. It is a prepayment for our food, not a bank account or a gift card, and it cannot be spent anywhere else or swapped for cash, except as set out below.',
        ],
      },
      {
        heading: 'Topping up',
        body: [
          '- Top up from Home → Top up with the amounts shown in the app, paid by card. Some top-ups come with bonus credit; the app shows it before you pay.',
          '- Your balance is held in your Tikkas account. Only the card payment goes through Stripe.',
        ],
      },
      {
        heading: 'Spending it',
        body: [
          '- Use credit for the whole order, or credit plus a card for the rest.',
          '- Paying with credit earns 10 Rewards Club points per £1 instead of 5.',
          '- Credit never expires while your account is open.',
          '- Credit belongs to your account and cannot be moved to another person.',
        ],
      },
      {
        heading: 'Refunds',
        body: [
          `- If an order paid with credit is refunded, the credit part goes back to your balance.`,
          '- Store credit cannot be refunded or exchanged for cash once bought. Bonus credit has no cash value.',
          '- If you delete your account, any credit left on it is lost, so spend it first.',
        ],
      },
      {
        heading: 'Changes',
        body: [`We may change the top-up amounts and bonuses for future top-ups. Credit already on your account keeps its value. Last updated ${LEGAL_UPDATED}.`],
      },
    ],
  },
  {
    slug: 'rewards',
    title: 'Rewards Club terms',
    summary: 'How you earn and spend Rewards Club points.',
    updated: LEGAL_UPDATED,
    sections: [
      {
        heading: 'Joining',
        body: ['The Rewards Club is free. Every Tikkas account is a member, and new members get 100 points when they join.'],
      },
      {
        heading: 'Earning points',
        body: [
          '- 5 points for every £1 you pay by card, and 10 points for every £1 you pay with store credit.',
          '- Points are added when you collect your order: in the app, or at the till when you scan your code.',
          '- Points are worked out on what you actually pay, after any discount. If an order is refunded, the points it earned come off.',
        ],
      },
      {
        heading: 'Rewards',
        body: [
          '- 250 points: a free side or dessert.',
          '- 1,000 points: a free Regular main.',
          'Rewards are free food, never cash or money off. Choose a reward in the bag, or show your code at the till. Points have no cash value and cannot be sold or moved to someone else.',
        ],
      },
      {
        heading: 'Refer a friend',
        body: ['Share your code. When a friend joins with it and collects their first order, you both get a reward (at the moment, free Tikki Tots). One reward per friend; a friend must be new to Tikkas.'],
      },
      {
        heading: 'Members\' groups',
        body: ['From time to time we may invite members to groups such as the Founders Club, with early access to new flavours and other extras. Invitations are at our discretion.'],
      },
      {
        heading: 'Fair use and changes',
        body: [
          'Points and rewards gained by misuse (for example, several accounts for one person) may be removed. Points do not expire while your account is open. We may change the Rewards Club, with at least 7 days\' notice in the app for changes that reduce what your points are worth. If we close the Rewards Club, we will give you at least 90 days to use your points.',
          `Last updated ${LEGAL_UPDATED}.`,
        ],
      },
    ],
  },
  {
    slug: 'catering',
    title: 'Catering terms',
    summary: 'Ordering Tikkas catering for your office or event: payment, delivery, changes, cancellations, allergens and invoices.',
    updated: LEGAL_UPDATED,
    sections: [
      {
        heading: 'Ordering',
        body: [
          '- Order by number of people, as individual bowls, or with a team link where each person picks their own lunch.',
          '- Order by 12:00 (midday) the day before. Next-day orders are limited by how much our kitchen can make; later dates have no limit.',
          '- Delivery is in 20-minute windows from 11:00, during our opening hours. Collection from the shop has no minimum.',
        ],
      },
      {
        heading: 'Paying',
        body: [
          '- You pay the full amount by card when you order; it is charged straight away. You can add a PO number, which appears on your VAT invoice.',
          '- Prices include VAT. A VAT invoice is ready in the app as soon as your order is booked, and is updated after any change or refund.',
        ],
      },
      {
        heading: 'Delivery',
        body: [
          '- Delivery is free within 1 mile of the shop for orders of £100 or more, by the Tikkas cargo bike.',
          '- Please give us accurate access details and an on-the-day contact who answers their phone. We hand over at the reception or floor you give us.',
        ],
      },
      {
        heading: 'Changes and cancellations',
        body: [
          '- Until the cut-off you can reduce quantities by up to 10% of the mains at no cost; the difference is refunded straight away.',
          '- Any other change is a request our team will answer in the app. A change that costs less is refunded; one that costs more is confirmed when you pay the difference.',
          '- Cancel before the cut-off: full refund. After the cut-off but before the day: half refunded. On the day: no refund, because the food has been bought and prepared.',
          '- Refunds go back to the card you paid with.',
        ],
      },
      {
        heading: 'Allergens',
        body: [
          'Every box is labelled with its contents and allergens, and we give you the allergen information in writing at delivery. Tell us about any allergy or dietary need when you order. Our kitchen handles all 14 major allergens, so we cannot guarantee any item is free of them. Our chicken is halal (HMC certified).',
        ],
      },
      {
        heading: 'Points and other terms',
        body: [
          'Catering orders earn Rewards Club points for the account that pays, added on delivery or collection. The terms of use also apply.',
          `Questions: message us from your order in the app, or email ${CONTACT}. Last updated ${LEGAL_UPDATED}.`,
        ],
      },
    ],
  },
  {
    slug: 'support',
    title: 'Contact and help',
    summary: 'How to reach Tikkas about an order, your account or the app.',
    updated: LEGAL_UPDATED,
    sections: [
      {
        heading: 'Talk to us',
        body: [
          `- Email: ${CONTACT}`,
          '- Phone: 020 8143 5502',
          `- In person: Tikkas, ${SHOP}, a short walk from Oxford Circus. Closed on Sundays.`,
          'For a catering order, message us from the order page in the app: the team sees it straight away.',
        ],
      },
      {
        heading: 'Common questions',
        body: [
          '- Signing in: enter your UK mobile number and the 6-digit code we text you. No password.',
          '- Collecting: show the code in the app at the counter, or give the backup code on your order screen.',
          '- Points: 5 per £1 by card, 10 per £1 with store credit, added when you collect.',
          '- Deleting your account: Account → Delete account. It takes effect straight away.',
          '- Notifications: switch order updates and offers on or off in Account → Notifications.',
        ],
      },
      {
        heading: 'Legal',
        body: ['Privacy policy, terms of use, store credit terms, Rewards Club terms and catering terms are all at app.tikkas.com/legal.'],
      },
    ],
  },
];

export function legalDoc(slug: string): LegalDoc | undefined {
  return LEGAL_DOCS.find((d) => d.slug === slug);
}

/** Text with [[placeholder]] marks split for display: even indexes are plain text, odd ones are placeholders. */
export function splitPlaceholders(text: string): string[] {
  return text.split(/\[\[(.*?)\]\]/g);
}
